import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/utils";
import type {
  ClientMySurveysQueryParams,
  ClientMySurveysApiResponse,
  ClientMySurveyItemResDto,
  ClientSurveyQuestionsApiResponse,
  ClientSurveyQuestionsDataDto,
  ClientSurveyBulkAnswersDto,
  ClientSurveyBulkAnswersApiResponse,
} from "../types/client-surveys.types";
import type { ClientPoll, ClientPollStatus } from "../../client-polls/types/client-polls.types";
import type { EducationFollowUpAction } from "@/features/client-educations/utils/follow-up-url";

const SURVEY_ME_ENDPOINT = "/api/client/educations/survey/me";

function getSurveyQuestionsEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/survey/questions`;
}

function getSurveyAnswersBulkEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/survey/answers/bulk`;
}

function mapApiItemToClientPoll(item: ClientMySurveyItemResDto): ClientPoll {
  const isPending = item.status === "pending";
  const status: ClientPollStatus = isPending ? "pending" : "answered";
  return {
    id: item.educationId,
    title: item.title,
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    isCompleted: !isPending,
    status,
  };
}

/**
 * GET /api/client/educations/survey/me
 * Kullanıcının anketlerini listeler (sayfalı, filtrelenebilir).
 *
 * Örnek: ?page=1&limit=12&status=pending&sortBy=startDate&sortOrder=desc
 */
export async function getMySurveys(
  params: ClientMySurveysQueryParams
): Promise<{ items: ClientPoll[]; count: number }> {
  const { data } = await apiClient.get<ClientMySurveysApiResponse>(
    SURVEY_ME_ENDPOINT,
    { params }
  );

  const rawItems = Array.isArray(data?.data) ? data.data : [];
  const count = data?.count ?? 0;

  return {
    items: rawItems.map(mapApiItemToClientPoll),
    count,
  };
}

/**
 * GET /api/client/educations/{educationId}/survey/questions
 * Anket sorularını ve seçeneklerini getirir.
 *
 * Backend alan adları (`selectedOptionID`, `selectedOptionIDs`, `textValue`)
 * frontend tipiyle (`selectedOptionIds`, `textAnswer`) birebir örtüşmüyor;
 * burada normalize ediyoruz ki modal "cevaplarımı gör" akışında önceki
 * cevaplar otomatik işaretli görünsün.
 */
export async function getSurveyQuestions(
  educationId: string
): Promise<ClientSurveyQuestionsDataDto> {
  const { data } = await apiClient.get<ClientSurveyQuestionsApiResponse>(
    getSurveyQuestionsEndpoint(educationId)
  );
  const payload = data?.data;
  const rawQuestions = Array.isArray(payload?.questions) ? payload.questions : [];
  const questions = rawQuestions.map((q) => {
    const r = q as unknown as Record<string, unknown>;
    const selectedSingle =
      (r.selectedOptionID as string | null | undefined) ??
      (r.selectedOptionId as string | null | undefined) ??
      null;
    const selectedMulti =
      (r.selectedOptionIDs as string[] | null | undefined) ??
      (r.selectedOptionIds as string[] | null | undefined) ??
      null;
    const textVal =
      (r.textValue as string | null | undefined) ??
      (r.textAnswer as string | null | undefined) ??
      null;

    const normalizedIds: string[] = Array.isArray(selectedMulti) && selectedMulti.length
      ? selectedMulti
      : selectedSingle
        ? [selectedSingle]
        : [];

    return {
      ...q,
      selectedOptionIds: normalizedIds,
      textAnswer: textVal ?? null,
    };
  });
  const attempt = payload?.attempt as ClientSurveyQuestionsDataDto["attempt"];
  return {
    attempt,
    questions,
    introText: payload?.introText ?? null,
    closingText: payload?.closingText ?? null,
  };
}

/**
 * PUT /api/client/educations/{educationId}/survey/answers/bulk
 * Anket cevaplarını toplu gönderir veya günceller.
 *
 * Response şekli (yeni): { answers: SurveyAnswerResDto[], followUpAction: ... | null }
 *   - followUpAction null + tüm zorunlular cevaplandı → enrollment COMPLETED, sertifika kuyruğa atıldı
 *   - followUpAction null + zorunlu eksik ve enrollment ENROLLED → hâlâ devam ediyor
 *   - followUpAction objesi → kullanıcıya bir sonraki popup (anket yeniden vb.) gösterilir
 */
export async function putSurveyAnswersBulk(
  educationId: string,
  payload: ClientSurveyBulkAnswersDto
): Promise<{ followUpAction: EducationFollowUpAction | null }> {
  const { data } = await apiClient.put<
    ClientSurveyBulkAnswersApiResponse & {
      data?: {
        followUpAction?: EducationFollowUpAction | null;
      };
      followUpAction?: EducationFollowUpAction | null;
    }
  >(getSurveyAnswersBulkEndpoint(educationId), payload);
  // BE envelope: { success, status, data: { answers, followUpAction }, ... }
  // Bazı uç noktalar data dışında düz alan da döndürebildiğinden iki konumu da kontrol ediyoruz.
  const envelope = data as {
    data?: { followUpAction?: EducationFollowUpAction | null };
    followUpAction?: EducationFollowUpAction | null;
  };
  const followUpAction =
    envelope?.data?.followUpAction ?? envelope?.followUpAction ?? null;
  return { followUpAction };
}

export const clientSurveysApi = {
  getMySurveys,
  getSurveyQuestions,
  putSurveyAnswersBulk,
};
